<template>
  <div class="about-us">
    <section class="intro">
      <h1>About Mudra</h1>

      <p>
        <strong>Mudra Creative Technologies</strong> is building a simple, creator-first platform in the digital creative space through our utility token — <strong>Mudra</strong>.
      </p>

      <p>
        We are a small, passionate team of developers, writers, and tech enthusiasts based in India. Our focus is to create a transparent and accessible home for writers and readers where original stories and poetry can be shared freely.
      </p>

      <p>
        Mudra Token serves as a utility and reward token within the ecosystem. It is awarded to creators <strong>only when they publish a story</strong> — recognizing the act of creation and contribution itself. Tokens are <strong>not</strong> distributed based on ratings, views, likes, or any other engagement metrics.
      </p>

      <p>
        From time to time, we may run contests with prize pools of Mudra Tokens awarded to winners according to the contest rules and decided prizes. These are the only additional ways tokens are distributed.
      </p>

      <div class="disclaimer-box">
        <strong>Important:</strong> Mudra is a <strong>pure utility and reward token</strong> — <strong>not</strong> an investment, security, or financial asset. It carries <strong>no promise</strong> of profit, value appreciation, speculation opportunity, or guaranteed returns. There is no financial commitment required to participate. Always do your own research (DYOR) and engage only with what you fully understand.
      </div>

      <p>
        At our core: <strong>creativity • honesty • fairness • community</strong>.<br>
        We aim to foster a space where writers can publish without gatekeepers, readers can discover authentic work, and contributions are acknowledged in a straightforward way.
      </p>
    </section>

    <section class="mission">
      <h2>Our Mission</h2>
      <p>
        To provide an inclusive, low-barrier platform that:
      </p>
      <ul>
        <li>Encourages free publishing of original stories and poetry</li>
        <li>Values the act of creation itself</li>
        <li>Supports everyday writers, especially from underrepresented regions</li>
        <li>Builds a genuine community around reading and sharing</li>
      </ul>
    </section>

    <section class="join">
      <h2>Join the Mudra Movement</h2>

      <p>
        Come write your story, read something new, or help us grow.<br>
        Your participation — whether publishing, reading, or giving feedback — helps shape this early-stage platform.
      </p>

      <div class="links">
        <a href="mailto:mudratoken25@gmail.com" aria-label="Email us">📧 mudratoken25@gmail.com</a>
        <a href="https://twitter.com/Mudra_token" target="_blank" rel="noopener noreferrer" aria-label="Follow on X">🕊️ @Mudra_token on X</a>
        <a href="https://t.me/+pcAStVBSRD44YzBl" target="_blank" rel="noopener noreferrer" aria-label="Join Telegram">💬 @mudratoken_community on Telegram</a>
      </div>

      <div class="feedback-button">
        <router-link to="/feedback">
          <button type="button">Share Your Feedback</button>
        </router-link>
      </div>

      <p class="small-note">
        We're in early development and value every suggestion right now.
      </p>
    </section>
  </div>
</template>

<script setup>
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Orbitron:wght@600;700&display=swap');

/* ── Page Wrapper ── */
.about-us {
  padding: 110px 24px 80px;
  max-width: 860px;
  margin: 0 auto;
  font-family: 'Inter', sans-serif;
  color: #d8eeff;
  line-height: 1.8;
}

/* ── Sections as Glass Cards ── */
section {
  background: rgba(8, 18, 36, 0.72);
  border: 1px solid rgba(0, 200, 255, 0.15);
  border-radius: 20px;
  padding: 40px 36px;
  margin-bottom: 28px;
  backdrop-filter: blur(14px);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.4), 0 0 30px rgba(0, 200, 255, 0.05);
  transition: border-color 0.3s ease;
}
section:hover {
  border-color: rgba(0, 200, 255, 0.3);
}

/* ── Headings ── */
h1 {
  font-family: 'Orbitron', sans-serif;
  font-size: 2.4rem;
  color: #00c8ff;
  text-align: center;
  margin-bottom: 1.6rem;
  text-shadow: 0 0 20px rgba(0, 200, 255, 0.5);
  letter-spacing: 1px;
}

h2 {
  font-family: 'Orbitron', sans-serif;
  font-size: 1.5rem;
  color: #00eaff;
  text-align: center;
  margin-bottom: 1.4rem;
  text-shadow: 0 0 12px rgba(0, 200, 255, 0.4);
  letter-spacing: 0.5px;
}

/* ── Paragraphs ── */
.intro p,
.mission p,
.join p {
  font-size: 1.02rem;
  margin-bottom: 1.2rem;
  color: #cde4ff;
}

strong {
  color: #00eaff;
  font-weight: 600;
}

/* ── Disclaimer Box ── */
.disclaimer-box {
  background: rgba(255, 193, 7, 0.08);
  border-left: 4px solid #ffd700;
  padding: 1.2rem 1.5rem;
  margin: 1.6rem 0;
  border-radius: 10px;
  font-size: 0.98rem;
  color: #fff3cd;
  line-height: 1.7;
}

/* ── Mission List ── */
.mission ul {
  list-style: none;
  padding-left: 0;
  margin: 1.2rem 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.mission li {
  background: rgba(0, 200, 255, 0.07);
  border: 1px solid rgba(0, 200, 255, 0.15);
  border-radius: 12px;
  padding: 14px 16px 14px 42px;
  position: relative;
  font-size: 0.96rem;
  color: #cde8ff;
  transition: all 0.25s ease;
}
.mission li:hover {
  background: rgba(0, 200, 255, 0.13);
  border-color: rgba(0, 200, 255, 0.35);
  transform: translateY(-2px);
}

.mission li::before {
  content: "✦";
  position: absolute;
  left: 14px;
  top: 14px;
  color: #00c8ff;
  font-size: 14px;
}

/* ── Contact Links ── */
.links {
  margin: 1.6rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.links a {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #00c8ff;
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  background: rgba(0, 200, 255, 0.08);
  border: 1px solid rgba(0, 200, 255, 0.2);
  padding: 10px 20px;
  border-radius: 30px;
  transition: all 0.25s ease;
}

.links a:hover {
  background: rgba(0, 200, 255, 0.18);
  border-color: rgba(0, 200, 255, 0.5);
  color: #80f0ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 14px rgba(0, 200, 255, 0.2);
}

/* ── Feedback Button ── */
.feedback-button {
  text-align: center;
  margin: 2rem 0 1rem;
}

.feedback-button button {
  background: linear-gradient(90deg, #00c8ff, #0066ff);
  color: #000;
  border: none;
  padding: 13px 36px;
  font-size: 1rem;
  font-weight: 700;
  font-family: 'Inter', sans-serif;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 18px rgba(0, 200, 255, 0.3);
  letter-spacing: 0.3px;
}

.feedback-button button:hover {
  background: linear-gradient(90deg, #00eaff, #0088ff);
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0, 200, 255, 0.45);
}

/* ── Small note ── */
.small-note {
  text-align: center;
  font-size: 0.88rem;
  color: #7ab0cc;
  margin-top: 0.8rem;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .about-us { padding: 90px 16px 60px; }
  h1 { font-size: 1.9rem; }
  h2 { font-size: 1.3rem; }
  section { padding: 28px 20px; }
  .mission ul { grid-template-columns: 1fr; }
}

@media (max-width: 480px) {
  h1 { font-size: 1.6rem; }
  .feedback-button button { width: 100%; max-width: 300px; }
}
</style>
